import React, { useCallback, useEffect, useState } from 'react';
import { apiBase, authHeaders, resolveOrgId } from './utils/authHeaders';

const API_URL = apiBase();

/** Pure — confirm one draft then remove from local list. */
export async function confirmDraftOptimistic(apiConfirm, recipeId, onRemove) {
  onRemove(recipeId);
  try {
    await apiConfirm(recipeId);
    return { ok: true };
  } catch (err) {
    return { ok: false, error: err };
  }
}

export default function ChefConfirm() {
  const [orgId] = useState(() => resolveOrgId());
  const [recipes, setRecipes] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/api/onboarding/recipes/drafts`, {
        headers: authHeaders({ orgId, json: true }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setRecipes(data.recipes || []);
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => {
    load();
  }, [load]);

  const confirmOne = async (recipeId) => {
    const apiConfirm = async (id) => {
      const res = await fetch(`${API_URL}/api/onboarding/recipes/${id}/confirm`, {
        method: 'POST',
        headers: authHeaders({ orgId, json: true }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return res.json();
    };
    const result = await confirmDraftOptimistic(apiConfirm, recipeId, (id) => {
      setRecipes((prev) => prev.filter((r) => r.id !== id));
    });
    if (!result.ok) {
      setError(result.error?.message || 'Confirm failed');
      await load();
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 py-8" data-testid="chef-confirm">
      <h1 className="text-2xl font-semibold text-slate-900 mb-2">Chef confirm</h1>
      <p className="text-slate-600 mb-6 text-sm">
        Review AI-drafted recipes from menu onboarding. Confirm to activate costing.
      </p>
      {loading && <p className="text-slate-500">Loading drafts…</p>}
      {error && <p className="text-rose-700 mb-4" role="alert">{error}</p>}
      {!loading && recipes.length === 0 && (
        <p className="text-slate-500">No draft recipes. Run menu onboarding first.</p>
      )}
      <ul className="space-y-4">
        {recipes.map((r) => (
          <li
            key={r.id}
            className="border border-slate-200 rounded p-4 bg-white"
            data-testid={`draft-recipe-${r.id}`}
          >
            <div className="flex justify-between items-start gap-4">
              <div>
                <h2 className="font-medium text-slate-900">{r.name}</h2>
                <ul className="mt-2 text-sm text-slate-600 list-disc pl-5">
                  {(r.lines || []).map((ln, i) => (
                    <li key={i}>
                      {ln.name} · {ln.qty} {ln.unit}
                    </li>
                  ))}
                </ul>
              </div>
              <button
                type="button"
                className="shrink-0 px-3 py-1.5 bg-teal-800 text-white text-sm font-medium"
                onClick={() => confirmOne(r.id)}
              >
                Confirm
              </button>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
